import { useCallback } from 'react'

import {
  UseBrowserNotificationSettingsResultIProps,
  useBrowserNotificationSettings,
} from './useBrowserNotificationSettings'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'

import {
  Notification as IncidentNotification,
  NotificationType,
} from '@/graphql/appsync-notifications'
import { useAppDispatch } from '@/redux/hooks'
import { openIncidentDrawer, setIncident } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { BROWSER_NOTIFICATION_ANALYTICS } from './constants'
import { buildBannerContent, getBrowserPermission, raiseBanner } from './utils'

export interface UseBrowserNotificationsResultIProps
  extends UseBrowserNotificationSettingsResultIProps {
  showIncidentBanner: (notification: IncidentNotification) => boolean
}

/**
 * OS banners for new Incidents, for an operator who has tabbed away.
 *
 * A supplement to the toast and audio alert, never a replacement: OS Focus
 * modes swallow banners undetectably and a closed browser raises nothing, so
 * this is not a guaranteed alerting channel.
 *
 * Mounted once by NotificationsProvider.
 */
export const useBrowserNotifications =
  (): UseBrowserNotificationsResultIProps => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isFeatureFlagOn = useFeatureFlag(FeatureFlag.browserNotifications)
    const settings = useBrowserNotificationSettings()
    const { isEnabled, isSupported } = settings

    /**
     * Callers must already have applied the in-app rules (mute, Critical
     * bypass, active-filter match). This adds the banner-only gates: flag on,
     * browser supports it, opted in, permission still granted (re-read, since
     * a user can revoke it without the app hearing), `incident.new` only, and
     * no HiveWatch tab visible anywhere — if one is, the toast already did it.
     */
    const showIncidentBanner = useCallback(
      (notification: IncidentNotification): boolean => {
        if (!isFeatureFlagOn || !isSupported || !isEnabled) {
          return false
        }

        if (getBrowserPermission() !== 'granted') {
          return false
        }

        if (notification?.notificationType !== NotificationType.IncidentNew) {
          return false
        }

        // Only this tab's visibility is considered. A hidden tab raising a
        // banner while the operator reads another tab is redundant, not
        // harmful — and `tag` collapses duplicates across tabs into one.
        // Suppressing on what *other* tabs are doing risked the opposite: an
        // Incident surfacing nowhere, because every tab evaluates mute and its
        // own active filter and neither syncs between tabs.
        if (typeof document !== 'undefined' && !document.hidden) {
          return false
        }

        const { body, title } = buildBannerContent(notification, t)

        return raiseBanner({
          action: 'show_banner',
          body,
          onClick: () => {
            // window.focus() works because this is a page-created
            // Notification, not a service-worker one.
            window.focus()
            dispatch(setIncident({ id: notification.incidentId }))
            dispatch(openIncidentDrawer())

            trackEvent(BROWSER_NOTIFICATION_ANALYTICS.bannerClicked, {
              incident_display_id: notification.incidentDisplayId,
              incident_id: notification.incidentId,
            })
          },
          // Collapses duplicates across tabs into one banner — the only
          // cross-tab dedupe needed, and the OS does it for free.
          tag: notification.incidentId,
          title,
        })
      },
      [dispatch, isEnabled, isFeatureFlagOn, isSupported, t]
    )

    return { ...settings, showIncidentBanner }
  }
